// @ts-nocheck

import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Navigation } from "swiper";
import { useRouter } from "next/router";
import { Grid } from "antd";

import { Flex, RATypography } from "@/components/customs";
import { getSettings } from "@/stores/slices/settingSlice";
import {
	SEChevronLeft,
	SEChevronRight,
	SEClock,
	SEPartnerFleetFavicon,
	SERAFleetFavicon,
	SESpeedometer,
	SEUserGroup,
} from "@/components/svgs";
import { AircraftOwnershipTag } from "@/components/molecules";
import { WorldMapRange } from "@/components/molecules/WorldMapRange";
import { AIRCRAFT_LOCATIONS, AIRCRAFT_PROVIDER } from "@/config/constants";
import s from "./AircraftSelectionMobile.module.css";
import { PATH_ROUTES } from "@/config/routes";
import { SvgArrowLeft } from "@/assets/icons";
import Image from "next/image";

const { useBreakpoint } = Grid;

export const AircraftSelectionMobile = ({ open, onUpdateOpen, onClose }: any) => {
	const swiperRef = useRef(null);
	const categorySwiperRef = useRef(null);
	const router = useRouter();
	const { sm } = useBreakpoint();

	const { aircraft_categories = [] } = useSelector(getSettings);

	const [selectedCategory, setSelectedCategory] = useState(aircraft_categories?.[0] ?? null);
	const [location, setLocation] = useState(AIRCRAFT_LOCATIONS.DETROIT);
	const [exploreFleet, setExploreFleet] = useState(false);
	const [categoryIndex, setCategoryIndex] = useState(0);
	const [activeIndex, setActiveIndex] = useState(0);

	const aircraftType = selectedCategory?.aircraft_type_list?.[activeIndex] ?? null;
	const isPartner = aircraftType?.aircraft_provider === AIRCRAFT_PROVIDER.PARTNER || !exploreFleet;
	const aircraftTypeLength = selectedCategory?.aircraft_type_list?.length ?? 0;

	useEffect(() => {
		if (swiperRef.current) {
			swiperRef.current.slideTo(activeIndex);
		}
	}, [activeIndex]);

	useEffect(() => {
		if (aircraft_categories?.length > 0) {
			setSelectedCategory(aircraft_categories[0]);
		}
	}, [aircraft_categories]);

	useEffect(() => {
		if (!open) {
			setSelectedCategory(aircraft_categories?.[0] ?? null);
			setLocation(AIRCRAFT_LOCATIONS.DETROIT);
			setExploreFleet(false);
			setActiveIndex(0);
			setCategoryIndex(0);
			categorySwiperRef.current?.slideTo(0, 0);
		}
	}, [open]);

	const handleSelectCategory = (c: any) => {
		setSelectedCategory(c);
		setActiveIndex(0);
	};

	const handleNavigateToTrustedPartners = () => {
		if (isPartner) {
			router.push(PATH_ROUTES.PATH_TRUSTED_PARTNERS);
		} else {
			if (aircraftType) {
				router.push(`${PATH_ROUTES.PATH_AIRCRAFT_TYPES}/${aircraftType.slug}`);
			}
		}
		onClose();
	};

	return (
		<div className={s.container}>
			<div>
				<div className={s.close_button} onClick={() => onUpdateOpen(false)}>
					<SvgArrowLeft color="#A8A8A8" />
					Menu
				</div>
				<Swiper
					className={s.category_swiper}
					slidesPerView="auto"
					centeredSlides={true}
					spaceBetween={8}
					grabCursor={true}
					speed={400}
					style={{ marginBottom: sm ? 16 : 12 }}
					onSwiper={(w) => {
						categorySwiperRef.current = w;
					}}
					onSlideChange={(w) => {
						setCategoryIndex(w.activeIndex);
						handleSelectCategory(aircraft_categories?.[w.activeIndex] ?? null);
					}}
					onBeforeDestroy={() => {
						categorySwiperRef.current = null;
					}}
				>
					{aircraft_categories?.map((item: any, index: number) => (
						<SwiperSlide key={item._id} className={s.category_slide}>
							<div
								className={s.category_card}
								style={{ borderColor: index === categoryIndex ? "#FFC34D" : "#E9E8E8" }}
							>
								<RATypography variant="gilda1" style={{ textTransform: "capitalize" }}>
									{item.name}
								</RATypography>
								<div className={s.category_specs}>
									<Flex alignItems="flex-end" gap={24}>
										<CategorySpec
											label="Range"
											value={`${item.min_fly_times_hours ?? 0}-${item.max_fly_times_hours ?? 0} Hours`}
										/>
										<CategorySpec
											label="Passengers"
											value={`${item.min_passengers ?? 0}-${item.max_passengers ?? 0}`}
										/>
									</Flex>
									<CategorySpec label="Cruise Speed" value={`${item.cruising_speed_mph ?? 0} Mph`} />
								</div>
							</div>
						</SwiperSlide>
					))}
				</Swiper>
				{aircraft_categories?.length > 1 && (
					<div className={s.category_pagination}>
						{aircraft_categories.map((item: any, index: number) => (
							<button
								key={item._id}
								type="button"
								aria-label={item.name}
								className={`${s.category_pagination_dot} ${
									index === categoryIndex ? s.category_pagination_dot_active : ""
								}`}
								onClick={() => categorySwiperRef.current?.slideTo(index)}
							/>
						))}
					</div>
				)}
				{!exploreFleet && (
					<Flex alignItems="center" gap={8}>
						{[AIRCRAFT_LOCATIONS.DETROIT, AIRCRAFT_LOCATIONS.MIAMI, AIRCRAFT_LOCATIONS.LOS_ANGELES].map(
							(loc) => (
								<button
									key={loc}
									className={s.location_button}
									style={{
										borderColor: location === loc ? "#ffb11a" : "#E9E8E8",
									}}
									onClick={() => setLocation(loc)}
								>
									{loc.replace(/_/g, " ")}
								</button>
							),
						)}
					</Flex>
				)}
			</div>
			<div style={{ flex: 1, position: "relative" }}>
				{exploreFleet ? (
					<div className={s.explore_fleet_wrapper}>
						<button
							className={s.swiper_button_prev}
							style={{
								visibility: aircraftTypeLength > 1 ? "visible" : "hidden",
							}}
						>
							<SEChevronLeft />
						</button>
						<button
							className={s.swiper_button_next}
							style={{
								visibility: aircraftTypeLength > 1 ? "visible" : "hidden",
							}}
						>
							<SEChevronRight />
						</button>
						<Swiper
							effect={"coverflow"}
							grabCursor={true}
							centeredSlides={true}
							slidesPerView={1}
							slidesPerGroup={1}
							coverflowEffect={{
								slideShadows: false,
								rotate: 0,
								stretch: -160,
								depth: 100,
								scale: 1,
							}}
							speed={1000}
							modules={[EffectCoverflow, Navigation]}
							navigation={{
								prevEl: `.${s.swiper_button_prev}`,
								nextEl: `.${s.swiper_button_next}`,
							}}
							onSwiper={(w) => {
								swiperRef.current = w;
							}}
							onSlideChange={(w) => setActiveIndex(w.activeIndex)}
							onBeforeDestroy={() => {
								swiperRef.current = null;
							}}
						>
							{selectedCategory?.aircraft_type_list?.map((item: any) => {
								const mainImage =
									item.images?.find((e) => e.type === "MAIN")?.url ??
									item.images?.[0]?.url ??
									"/images/example/aircraft_light_turbo.png";

								return (
									<SwiperSlide key={item._id} className={s.explore_fleet_swiper_slide}>
										<Flex alignItems="center" gap={12}>
											<div style={{ width: "100%" }}>
												<Flex alignItems="center" gap={8}>
													<AircraftOwnershipTag isPartner={isPartner} />
													<RATypography variant="sub-heading3">{item.name}</RATypography>
												</Flex>
												<div className={s.explore_fleet_image_wrapper}>
													<div className={s.explore_fleet_image}>
														<Image
															fill
															sizes="100vw"
															src={mainImage}
															alt={item.name}
															style={{
																objectFit: "contain",
																objectPosition: "center",
															}}
														/>
													</div>
												</div>
												<Flex
													alignItems="center"
													justifyContent="center"
													className={s.explore_fleet_swiper_slide_info}
												>
													<div>
														<Flex gap={16}>
															<SEUserGroup />
															<Flex alignItems="center">
																<RATypography variant="gilda1">
																	{item.properties?.max_pax ?? 0}
																	{item.properties?.max_pax_symbol ?? ""}
																</RATypography>
																<RATypography variant="dm_sans1" left={8}>
																	{item.properties?.max_pax_unit}
																</RATypography>
															</Flex>
														</Flex>
														<Flex gap={16} style={{ marginBlock: sm ? 12 : 8 }}>
															<SEClock />
															<Flex alignItems="center">
																<RATypography variant="gilda1">
																	{item.properties?.max_fly_time ?? 0}
																	{item.properties?.max_fly_time_symbol ?? ""}{" "}
																	{item.properties?.max_fly_time_unit}
																</RATypography>
																<RATypography variant="dm_sans1" left={8}>
																	Range
																</RATypography>
															</Flex>
														</Flex>
														<Flex gap={16}>
															<SESpeedometer />
															<Flex alignItems="center">
																<RATypography variant="gilda1">
																	{item.properties?.cruising_speed ?? 0}
																	{item.properties?.cruising_speed_symbol ?? ""}{" "}
																	{item.properties?.cruising_speed_unit}
																</RATypography>
																<RATypography variant="dm_sans1" left={8}>
																	Cruise Speed
																</RATypography>
															</Flex>
														</Flex>
													</div>
												</Flex>
											</div>
										</Flex>
									</SwiperSlide>
								);
							})}
						</Swiper>
					</div>
				) : (
					<div className={s.category_map}>
						{selectedCategory && (
							<WorldMapRange
								showAirportName={false}
								location={location}
								type={selectedCategory.classtype}
							/>
						)}
					</div>
				)}
			</div>
			<div style={{ zIndex: 1 }}>
				<button className={s.explore_royal_air_fleet_button} onClick={() => setExploreFleet(!exploreFleet)}>
					{exploreFleet ? (
						<>
							<SEChevronLeft />
							<span>Back To Category</span>
						</>
					) : (
						<>
							<SERAFleetFavicon />
							<span>Explore Royal Air’s Fleet</span>
							<SEChevronRight />
						</>
					)}
				</button>
				<button
					className={isPartner ? s.explore_partner_fleet_button : s.explore_royal_air_fleet_button}
					onClick={handleNavigateToTrustedPartners}
					style={{ marginTop: 12 }}
				>
					{isPartner ? (
						<>
							<SEPartnerFleetFavicon />
							<span>Learn About Our Trusted Partners</span>
						</>
					) : (
						<>
							<SERAFleetFavicon />
							<span>Learn More About This Aircraft</span>
						</>
					)}
					<SEChevronRight />
				</button>
			</div>
		</div>
	);
};

const CategorySpec = ({ label, value }: any) => (
	<Flex alignItems="flex-end" gap={8}>
		<RATypography variant="dm_sans1" style={{ opacity: 0.6 }}>
			{label}
		</RATypography>
		<RATypography variant="dm_sans1" style={{ fontWeight: 500 }}>
			{value}
		</RATypography>
	</Flex>
);
